import React, { useMemo, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { submit } from 'redux-form';
import moment from 'moment';
import { SLASHED_US_DATE_FORMAT } from '../../../constants/formats';
import Spinner from '../../../components/Spinner/Spinner';
import selectors from './memberPaymentsSelectors';
import NewTable from '../../../components/NewInfoBox/NewTable';
import resources from './memberPaymentsResources';
import TableFiltersPanel from '../../../components/NewInfoBox/TableFiltersPanel';
import MemberPaymentsFiltersForm from './MemberPaymentsFiltersForm';
import actions from './memberPaymentsActions';
import { getPeriodsUntilCurrent, getClosedUsagePeriods } from '../../Shared/Periods/periodsActions';
import CurrencyCell from '../../../components/NewInfoBox/CurrencyCell';
import BulletCell from '../../../components/NewInfoBox/BulletCell';
import { paymentStatusesColorsMap, paymentStatusesNamesMap } from '../../../enums/paymentStatuses';
import programPaymentTypesEnum from '../../../enums/programPaymentTypes';
import paymentTypes from '../../../enums/paymentTypes';
import { memberBillingTypesNamesMap } from '../../../enums/memberBillingTypes';
import { isTabletSize } from '../../../utilities/windowSize';
import styles from './_member-payments.module.scss';
import MuiButton from '../../../components/controls/MuiButton';
import Button from '../../../components/controls/Button';
import MaterialIcon from '../../../components/MaterialIcon/MaterialIcon';
import ConnectedConfirmPopup from '../../../components/Popups/ConfirmAlert/ConnectedConfirmPopup';
import wellnessProgramPaymentResources from '../wellnessProgramPaymentsResources';
import PaymentConfirmationForm from '../PaymentConfirmationForm/PaymentConfirmationForm';
import { setGlobalErrorMessage } from '../../App/actions';

function MemberPaymentsTab({
    payments,
    totalPaymentsAmount,
    memberPaymentsCount,
    isLoading,
    programPaymentTypes,
    initialFilterValues,
    memberPaymentTypes,
    getPartnersListByProgramPaymentType,
    programTypeID,
    paymentTypeID,
    partnerID,
    periodID,
    partners,
    getPeriodsUntilCurrent,
    getClosedUsagePeriods,
    periods,
    setPeriodID,
    setProgramTypeID,
    getPayments,
    downloadPayments,
    isViewButtonDisabled,
    popupMessage,
    popupSubmitText,
    checkMemberPaymentsProcessed,
    sendMemberPayments,
    popupData,
    getPaymentTypesByProgramPaymentType,
    currentPeriodId,
    setGlobalErrorMessage }) {
    const tableRef = useRef();
    const isTablet = isTabletSize();
    const columns = useMemo(
        () => [
            {
                Header: resources.columnNameEmployeeName,
                accessor: resources.accessorEmployeeName,
                sticky: isTablet ? 'left' : null,
                left: 0,
                sortType: 'caseInsensitiveAlphaNumeric'
            },
            {
                Header: resources.columnNameFacilityId,
                accessor: resources.accessorFacilityId,
                sortType: 'caseInsensitiveAlphaNumeric'
            },
            {
                Header: paymentTypeID === paymentTypes.gmMemberReimbursements ? resources.columnNameAmountToBeDeposited : resources.columnNameAmountToBeWithdrawn,
                accessor: resources.accessorAmountToBeWithdrawn,
                Cell: CurrencyCell
            },
            {
                Header: resources.columnNameMemberPaymentType,
                accessor: resources.accessorMemberPaymentType,
                Cell: cell => memberBillingTypesNamesMap[cell.value]
            },
            {
                Header: resources.columnNameBillingInfo,
                accessor: resources.accessorBillingInfo
            },
            {
                Header: resources.columnNameStatus,
                accessor: resources.accessorStatus,
                Cell: cell => BulletCell({ ...cell, value: paymentStatusesNamesMap[cell.value] }),
                getProps: row => ({
                    theme: paymentStatusesColorsMap[row.original.paymentStatusID]
                }),
            },
            {
                Header: resources.columnNameDate,
                accessor: resources.accessorDate,
                Cell: cell => moment(cell.value).format(SLASHED_US_DATE_FORMAT)
            }
        ],
        [resources, isTablet, paymentTypeID]
    );

    useEffect(() => {
        if (programTypeID) {
            getPartnersListByProgramPaymentType(programTypeID);
            getPaymentTypesByProgramPaymentType(programTypeID);
        }
    }, [programTypeID]);

    useEffect(() => {
        if (programTypeID && paymentTypeID) {
            setPeriods(programTypeID, paymentTypeID);
        }
    }, [programTypeID, paymentTypeID]);

    useEffect(() => {
        setGlobalErrorMessage(
            isMemberDuesSendingLocked()
                ? resources.monthlyDuesCollectingIsLocked
                : null);
        // run when unmount
        return () => {
            setGlobalErrorMessage(null);
        };
    }, [paymentTypeID, periodID, currentPeriodId]);

    const exportToExcel = () => {
        if (programTypeID && paymentTypeID && periodID) {
            downloadPayments(programTypeID, paymentTypeID, partnerID, periodID);
        }
    };

    const programPaymentTypeChangeHandler = (programPaymentTypeID) => {
        setProgramTypeID(programPaymentTypeID);
    };

    const setPeriods = (programPaymentTypeID, paymentTypeID) => {
        if (programPaymentTypeID === programPaymentTypesEnum.memberPaid && paymentTypeID === paymentTypes.gmMemberReimbursements) {
            getClosedUsagePeriods();
        }
        else {
            getPeriodsUntilCurrent();
        }
        setPeriodID();
    };

    const [submittedFilters, setSubmittedFilters] = useState({});
    const handleSubmit = (filters) => {
        filters = filters.toJS();
        getPayments(filters.programTypeID, filters.paymentTypeID, filters.partnerID, filters.periodID);
        setSubmittedFilters(filters);
    };
    const employeeNameAccessor = (item) => {
        return item.memberFullName;
    };
    const [isPopupOpened, setIsPopupOpened] = useState(false);
    const handlePINSubmit = (confirmationData) => {
        sendMemberPayments(
            programTypeID, paymentTypeID, partnerID, periodID,
            confirmationData.get('pin')).then(() => getPayments(programTypeID, paymentTypeID, partnerID, periodID));
        setIsPopupOpened(false);
    };
    const handleSend = () => {
        checkMemberPaymentsProcessed(programTypeID, paymentTypeID, partnerID, periodID).then(() => {
            setIsPopupOpened(true);
        });
    };

    const isSendAchLocked = () =>
        submittedFilters.paymentTypeID
            && submittedFilters.periodID  && currentPeriodId
            && submittedFilters.paymentTypeID === paymentTypes.gmMemberDues
            && submittedFilters.periodID !== currentPeriodId;

    const isMemberDuesSendingLocked = () =>
        paymentTypeID && periodID && currentPeriodId
        && paymentTypeID === paymentTypes.gmMemberDues
        && periodID !== currentPeriodId;

    const tableButton = <MuiButton disabled={isSendAchLocked()} className="btn_new btn_new--blue" onClick={handleSend} noPadding>
        {resources.buttonSend}
    </MuiButton>;
    const memberPaymentWithdrawalConfirmationForm = 'memberPaymentWithdrawalConfirmationForm';
    return <div className="member-payments">
        {isLoading ? <Spinner /> : null}
        {
            <ConnectedConfirmPopup
                showPopup={isPopupOpened}
                title={wellnessProgramPaymentResources.titleConfirmACHUpload}
                body={
                    <PaymentConfirmationForm
                        message={popupMessage}
                        form={memberPaymentWithdrawalConfirmationForm}
                        initialValues={popupData}
                        onSubmit={handlePINSubmit}
                    />
                }
                onConfirm={(dispatch) => {
                    dispatch(submit(memberPaymentWithdrawalConfirmationForm));
                }}
                onCancel={() => setIsPopupOpened(false)}
                labelOk={popupSubmitText}
                labelCancel={wellnessProgramPaymentResources.buttonCancel}
            />
        }
        <TableFiltersPanel>
            <MemberPaymentsFiltersForm
                programPaymentTypes={programPaymentTypes}
                onProgramPaymentTypeChange={programPaymentTypeChangeHandler}
                paymentTypes={memberPaymentTypes}
                partners={partners}
                periods={periods}
                initialValues={initialFilterValues}
                viewButtonDisabled={isViewButtonDisabled}
                onSubmit={handleSubmit}
            />
        </TableFiltersPanel>

        <div className="grid__row">
            <div className="col-lg-3">
                <div className={styles.member_payments_total}>{resources.headerTotalDuesAmount}: {totalPaymentsAmount}</div>
            </div>
            <div className="col-lg-9">
                <div className={styles.download_button_container}>
                    <Button className="btn_new btn_new--white btn_new--bordered" onClick={exportToExcel} disabled={isViewButtonDisabled}>
                        <MaterialIcon className="btn_new-icon" icon="download" />
                    </Button>
                </div>
            </div>
        </div>

        <NewTable
            columns={columns}
            initialData={payments}
            getItemId={employeeNameAccessor}
            initialSortBy={{
                id: resources.accessorEmployeeName,
                desc: false
            }}
            totalCount={memberPaymentsCount}
            ref={tableRef}
            manualSortBy={false}
            tableButton={tableButton}
        />
    </div>;
}

MemberPaymentsTab.propTypes = {
    initialFilterValues: PropTypes.object.isRequired,
    programPaymentTypes: PropTypes.object.isRequired,
    memberPaymentTypes: PropTypes.object.isRequired,
    partners: PropTypes.object.isRequired,
    periods: PropTypes.object.isRequired,
    payments: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    totalPaymentsAmount: PropTypes.string.isRequired,
    memberPaymentsCount: PropTypes.number.isRequired,
    getPartnersListByProgramPaymentType: PropTypes.func.isRequired,
    programTypeID: PropTypes.number,
    paymentTypeID: PropTypes.number,
    partnerID: PropTypes.number,
    periodID: PropTypes.number,
    getPeriodsUntilCurrent: PropTypes.func.isRequired,
    getClosedUsagePeriods: PropTypes.func.isRequired,
    defaultPeriodID: PropTypes.number,
    setPeriodID: PropTypes.func.isRequired,
    setProgramTypeID: PropTypes.func.isRequired,
    getPayments: PropTypes.func.isRequired,
    downloadPayments: PropTypes.func.isRequired,
    isViewButtonDisabled: PropTypes.bool.isRequired,
    popupMessage: PropTypes.string,
    popupSubmitText: PropTypes.string,
    checkMemberPaymentsProcessed: PropTypes.func.isRequired,
    sendMemberPayments: PropTypes.func.isRequired,
    popupData: PropTypes.object.isRequired,
    getPaymentTypesByProgramPaymentType: PropTypes.func.isRequired,
    currentPeriodId: PropTypes.number,
    setGlobalErrorMessage: PropTypes.func
};

const mapDispatchToProps = {
    ...actions,
    getPeriodsUntilCurrent,
    getClosedUsagePeriods,
    setGlobalErrorMessage
};

export default connect(selectors, mapDispatchToProps)(MemberPaymentsTab);
